import React, { useState, useEffect } from 'react';
import { useAuth, shouldShowAccountSettings, isAgencyUser } from '../../contexts/AuthContext';
import { useReseller } from '../../contexts/ResellerContext';
import NotAuthorized from '../common/NotAuthorized';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  Button,
  Tabs,
  Tab,
  Alert,
  CircularProgress
} from '@mui/material';
import ContactInfoSection from './sections/ContactInfoSection';
import BillingInfoSection from './sections/BillingInfoSection';
import PlanInfoSection from './sections/PlanInfoSection';
import TeamManagement from './TeamManagement';
import ModelSelectionSection from './sections/ModelSelectionSection';
import axios from 'axios';
import { API_URL } from '../../common/Constants';

const AccountSettings = () => {
  const { user } = useAuth();
  const { reseller } = useReseller();
  const showStripe = reseller == null || reseller.useStripeForBilling !== false;
  const location = useLocation();
  const navigate = useNavigate();
  // Support tab selection via query parameter
  const params = new URLSearchParams(location.search);
  const tabParam = params.get('tab');
  const [activeTab, setActiveTab] = useState(() => {
    if (tabParam === 'plan-information') return 0;
    if (tabParam === 'account-information') return 1;
    if (tabParam === 'ai-model-information') return 2;
    if (tabParam === 'team-management') return 3;
    return 0; // Plan Information is default first tab
  });
  const [stripePaymentPending, setStripePaymentPending] = useState(false);
  const [accountData, setAccountData] = useState({
    // Contact Information
    firstName: '',
    lastName: '',
    company: '',
    email: '',
    phone: '',
    // Billing Information
    useSameContactInfo: true,
    billingFirstName: '',
    billingLastName: '',
    billingCompany: '',
    billingAddress: '',
    billingAddress2: '',
    billingCity: '',
    billingState: '',
    billingZip: '',
    billingPhone: '',
    // Credit Card Information
    cardNumber: '',
    cardExpiry: '',
    cardCVV: '',
    cardName: '',
    // Plan Information
    currentPlan: 'S',
    planStatus: 'Active',
    signupDate: new Date().toISOString(),
    // Team Information
    teamMembers: []
  });

  const fetchAccountPlan = () => {
    axios.get(`${API_URL}/account/plan`)
      .then(response => {
        if (response.data) {
          let acctData = response.data;
          setAccountData(prev => ({ ...prev, currentPlan: acctData.plan, billing_cycle: acctData.billing_cycle }));
        }
      })
      .catch(error => {
        console.error('Error fetching account settings:', error);
      });
  };

  useEffect(() => {
    if (user && user.owner && !shouldShowAccountSettings(user)) {
      navigate('/Dashboard', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchAccountPlan();

    // When returning from Stripe (session_id in URL), webhook may still be processing. Show banner and refetch plan.
    const sessionId = params.get('session_id') || (() => {
      try {
        const hashQuery = (window.location.hash || '').split('?')[1] || '';
        return new URLSearchParams(hashQuery).get('session_id');
      } catch {
        return null;
      }
    })();
    if (sessionId) {
      setStripePaymentPending(true);
      const t1 = setTimeout(fetchAccountPlan, 2000);
      const t2 = setTimeout(fetchAccountPlan, 5000);
      const t3 = setTimeout(fetchAccountPlan, 8000);
      const clearBanner = setTimeout(() => {
        setStripePaymentPending(false);
        navigate(location.pathname || '/AccountSettings', { replace: true });
      }, 12000);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
        clearTimeout(t3);
        clearTimeout(clearBanner);
      };
    }
    // if (savedData) {
    //   setAccountData(JSON.parse(savedData));
    // } else if (signupData) {
    //   // Pre-populate from signup form
    //   const parsedSignupData = JSON.parse(signupData);
    //   setAccountData(prevData => ({
    //     ...prevData,
    //     firstName: parsedSignupData.firstName || '',
    //     lastName: parsedSignupData.lastName || '',
    //     company: parsedSignupData.company || '',
    //     email: parsedSignupData.email || '',
    //     phone: parsedSignupData.phone || '',
    //     currentPlan: parsedSignupData.selectedPlan || 'S'
    //   }));
    // }
  }, []);

  // useEffect(() => {
  //   //    localStorage.setItem('accountData', JSON.stringify(accountData));
  //   window.accountData = accountData;
  //   console.log('Account Settings Data:', accountData);
  // }, [accountData]);

  // Wait for user to load
  if (user === undefined || user === null) {
    return null; // or a loading spinner
  }
  if (!user.owner && !isAgencyUser(user)) {
    const ownerEmail = user.ownerEmail || '';
    return <NotAuthorized ownerEmail={ownerEmail} />;
  }
  if (!shouldShowAccountSettings(user)) {
    return null;
  }

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // ...existing code...

  // Handle changes to form fields
  const handleChange = (section, field, value) => {
    setAccountData(prevData => {
      // Special handling for useSameContactInfo toggle
      if (section === 'billing' && field === 'useSameContactInfo') {
        if (value) {
          // Copy contact info to billing info
          return {
            ...prevData,
            useSameContactInfo: value,
            billingFirstName: prevData.firstName,
            billingLastName: prevData.lastName,
            billingCompany: prevData.company,
            billingPhone: prevData.phone
          };
        } else {
          return {
            ...prevData,
            useSameContactInfo: value
          };
        }
      }

      // Normal field update
      return {
        ...prevData,
        [field]: value
      };
    });
  };

  // Handle team member operations
  const handleTeamMemberOperation = (operation, member = null, index = null) => {
    setAccountData(prevData => {
      const updatedTeamMembers = [...prevData.teamMembers];

      switch (operation) {
        case 'add':
          updatedTeamMembers.push(member);
          break;
        case 'update':
          if (index !== null) {
            updatedTeamMembers[index] = member;
          }
          break;
        case 'remove':
          if (index !== null) {
            updatedTeamMembers.splice(index, 1);
          }
          break;
        default:
          break;
      }

      return {
        ...prevData,
        teamMembers: updatedTeamMembers
      };
    });
  };

  // Handle plan changes
  const handlePlanChange = (newPlan, newCycle) => {
    setAccountData(prevData => ({
      ...prevData,
      currentPlan: newPlan,
      ...(newCycle != null && { billing_cycle: newCycle })
    }));
  };

  // Handle saving individual sections
  const handleSaveSection = (section) => {
    console.log(`${section} settings saved:`, accountData);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Account Settings</Typography>
      <Divider sx={{ mb: 2 }} />

      {showStripe && stripePaymentPending && (
        <Alert
          severity="success"
          icon={<CircularProgress size={20} color="inherit" />}
          sx={{ mb: 3 }}
        >
          Payment successful! Your plan is being updated. This usually takes a few seconds. The page will refresh automatically.
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="account settings tabs">
          <Tab label="Plan Information" />
          <Tab label="Account Information" />
          <Tab label="AI Model Information" />
          <Tab label="Team Management" />
        </Tabs>
      </Box>

      {/* Tab 0: Plan Information */}
      {activeTab === 0 && (
        <Grid container spacing={4} justifyContent="center">
          <Grid item xs={12} md={10} lg={8}>
            <PlanInfoSection
              accountData={accountData}
              handlePlanChange={handlePlanChange}
              handleCycleChange={(newCycle) => setAccountData(prev => ({ ...prev, billing_cycle: newCycle }))}
              onRefreshAccount={fetchAccountPlan}
            />
          </Grid>
        </Grid>
      )}

      {/* Tab 1: Account Information */}
      {activeTab === 1 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Contact Information Section */}
          <ContactInfoSection
            accountData={accountData}
            handleChange={handleChange}
            onSave={() => handleSaveSection('Contact')}
          />

          {/* Billing Information Section */}
          <BillingInfoSection
            accountData={accountData}
            handleChange={handleChange}
            onSave={() => handleSaveSection('Billing')}
          />
        </Box>
      )}

      {/* Tab 2: AI Model Information */}
      {activeTab === 2 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <ModelSelectionSection />
        </Box>
      )}

      {/* Tab 3: Team Management */}
      {activeTab === 3 && (
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <TeamManagement />
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default AccountSettings;